<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { Building } from "@types"

// ================================
// TYPES & INTERFACES
// ================================
type BuildingDpe = Building["infos"]["batiment"]["dpe"]

type DpeScaleItem = {
    letter: string
    range: string
    color: string
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        modelValue?: BuildingDpe | null
        readonly?: boolean
        /** Bâtiment en consultation seule : désactivé, pas seulement `readonly`. */
        disabled?: boolean
        displayHint?: boolean
    }>(),
    {
        modelValue: null,
        readonly: false,
        disabled: false,
        displayHint: false,
    }
)

const emit = defineEmits<{
    (e: "update:modelValue", value: BuildingDpe): void
}>()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            energyLabel: "DPE conso énergétiques",
            energyHint: "Donnée récupérée depuis la base des DPE",
            energySuffix: "kWh/m2/an",
            gesLabel: "DPE Emission GES",
            gesHint: "Donnée récupérée depuis la base des DPE",
            gesSuffix: "kg/m2/an",
            dateLabel: "Date de réalisation du DPE énergie",
            dateHint: "Donnée récupérée depuis la base des DPE",
        },
    },
})

// ================================
// COMPUTED
// ================================
const getModelValue = computed<BuildingDpe>(() => ({
    energie: {
        value: props.modelValue?.energie?.value ?? null,
        date: props.modelValue?.energie?.date ?? null,
    },
    ges: {
        value: props.modelValue?.ges?.value ?? null,
        date: props.modelValue?.ges?.date ?? null,
    },
}))

const getEnergyScale = computed(() =>
    getEnergyScaleItem(getModelValue.value.energie.value)
)

const getGesScale = computed(() =>
    getGesScaleItem(getModelValue.value.ges.value)
)

const getDateValue = computed<Date | null>(() => {
    const currentDate =
        getModelValue.value.energie.date ?? getModelValue.value.ges.date
    return parseDateValue(currentDate)
})

const getEnergyScaleTextClass = computed(() =>
    getContrastTextClass(getEnergyScale.value.color)
)

const getGesScaleTextClass = computed(() =>
    getContrastTextClass(getGesScale.value.color)
)

// ================================
// BUSINESS LOGIC
// ================================
const NAMED_COLORS_RGB: Record<string, [number, number, number]> = {
    lightgrey: [211, 211, 211],
}

/**
 * Parse une couleur CSS (hex ou nom connu) en RGB, ou `null` si non reconnue.
 * @param color Valeur `backgroundColor` (ex. `#349f2c`, `lightgrey`).
 * @returns Tuple RGB 0–255 ou `null`.
 */
function parseCssColorToRgb(color: string): [number, number, number] | null {
    const trimmed = color.trim().toLowerCase()
    if (trimmed.startsWith("#")) {
        const hex = trimmed.slice(1)
        if (hex.length === 3) {
            return [
                parseInt(hex[0] + hex[0], 16),
                parseInt(hex[1] + hex[1], 16),
                parseInt(hex[2] + hex[2], 16),
            ]
        }
        if (hex.length === 6) {
            return [
                parseInt(hex.slice(0, 2), 16),
                parseInt(hex.slice(2, 4), 16),
                parseInt(hex.slice(4, 6), 16),
            ]
        }
        return null
    }
    const named = NAMED_COLORS_RGB[trimmed]
    return named ? [...named] : null
}

/**
 * Indique si le texte doit être blanc sur ce fond (luminosité perçue basse).
 * @param backgroundColor Couleur de fond CSS.
 * @returns Classe Tailwind pour la couleur du texte.
 */
function getContrastTextClass(backgroundColor: string): string {
    const rgb = parseCssColorToRgb(backgroundColor)
    if (!rgb) return "text-surface-900"
    const [r, g, b] = rgb
    const brightness = (r * 299 + g * 587 + b * 114) / 1000
    return brightness < 128 ? "text-white" : "text-surface-900"
}

/**
 * Retourne le badge d'energie avec les memes seuils et labels que le legacy.
 * @param value Valeur du DPE energie.
 * @returns Badge a afficher.
 */
function getEnergyScaleItem(value: number | null | undefined): DpeScaleItem {
    switch (true) {
        case typeof value === "number" && value >= 0 && value <= 69:
            return { letter: "A", range: "0 à 69", color: "#349f2c" }
        case typeof value === "number" && value >= 70 && value < 110:
            return { letter: "B", range: "70 à 109", color: "#36d324" }
        case typeof value === "number" && value >= 110 && value < 180:
            return { letter: "C", range: "110 à 179", color: "#cdff02" }
        case typeof value === "number" && value >= 180 && value < 250:
            return { letter: "D", range: "180 à 249", color: "#ffff00" }
        case typeof value === "number" && value >= 250 && value < 330:
            return { letter: "E", range: "25O à 329", color: "#ffd000" }
        case typeof value === "number" && value >= 330 && value < 420:
            return { letter: "F", range: "330 à 420", color: "#ff9a21" }
        case typeof value === "number" && value >= 420:
            return { letter: "G", range: "> à 420", color: "#ff0000" }
        default:
            return { letter: "H", range: "> à 420", color: "lightgrey" }
    }
}

/**
 * Retourne le badge GES avec les memes seuils et labels que le legacy.
 * @param value Valeur du DPE GES.
 * @returns Badge a afficher.
 */
function getGesScaleItem(value: number | null | undefined): DpeScaleItem {
    switch (true) {
        case typeof value === "number" && value >= 0 && value < 6:
            return { letter: "A", range: "0 à 5", color: "#f6eefd" }
        case typeof value === "number" && value >= 6 && value < 11:
            return { letter: "B", range: "6 à 10", color: "#e0c2f8" }
        case typeof value === "number" && value >= 11 && value < 30:
            return { letter: "C", range: "11 à 29", color: "#d4aaf6" }
        case typeof value === "number" && value >= 30 && value < 50:
            return { letter: "D", range: "30 à 49", color: "#cb95f3" }
        case typeof value === "number" && value >= 50 && value < 70:
            return { letter: "E", range: "50 à 69", color: "#ba72ef" }
        case typeof value === "number" && value >= 70 && value < 100:
            return { letter: "F", range: "70 à 99", color: "#a74deb" }
        case typeof value === "number" && value >= 100:
            return { letter: "G", range: "> à 100", color: "#8a18df" }
        default:
            return { letter: "H", range: "> à 100", color: "lightgrey" }
    }
}

/**
 * Convertit une date issue de l'API en objet Date pour le datepicker.
 * @param value Date source.
 * @returns Date exploitable par le composant ou `null`.
 */
function parseDateValue(value: string | null | undefined): Date | null {
    if (!value) return null
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Convertit une date du datepicker en ISO string pour le backend.
 * @param value Valeur datepicker.
 * @returns Date ISO ou `null`.
 */
function formatDateForApi(
    value: Date | Date[] | null | undefined
): string | null {
    if (!value || Array.isArray(value)) return null
    return Number.isNaN(value.getTime()) ? null : value.toISOString()
}

/**
 * Construit le bloc DPE a reemettre au parent apres edition.
 * @param next Partial a fusionner avec la valeur courante.
 * @returns Bloc DPE complet.
 */
function buildNextModel(next: Partial<BuildingDpe>): BuildingDpe {
    return {
        energie: {
            ...getModelValue.value.energie,
            ...(next.energie ?? {}),
        },
        ges: {
            ...getModelValue.value.ges,
            ...(next.ges ?? {}),
        },
    }
}

// ================================
// INTERACTIONS
// ================================
/**
 * Met a jour la valeur de consommation energetique.
 * @param value Nouvelle valeur.
 * @returns Void.
 */
function onUpdateEnergyValue(value: number | null): void {
    emit(
        "update:modelValue",
        buildNextModel({
            energie: {
                value,
            },
        })
    )
}

/**
 * Met a jour la valeur d'emission GES.
 * @param value Nouvelle valeur.
 * @returns Void.
 */
function onUpdateGesValue(value: number | null): void {
    emit(
        "update:modelValue",
        buildNextModel({
            ges: {
                value,
            },
        })
    )
}

/**
 * Met a jour la date du DPE pour energie et GES.
 * @param value Nouvelle date.
 * @returns Void.
 */
function onUpdateDate(value: Date | Date[] | null | undefined): void {
    const date = formatDateForApi(value)
    emit(
        "update:modelValue",
        buildNextModel({
            energie: {
                date,
            },
            ges: {
                date,
            },
        })
    )
}
</script>

<template>
    <div
        class="building-info-general-dpe grid grid-cols-1 gap-4 lg:grid-cols-3"
    >
        <!-- ================================ -->
        <!-- DPE ENERGIE -->
        <!-- ================================ -->
        <BaseInputWrapper
            input-id="building-info-general-dpe-energy"
            :label="t('energyLabel')"
            :hint="displayHint ? t('energyHint') : ''"
            unfloat
        >
            <div
                class="flex min-w-0 flex-col gap-2 lg:flex-row lg:items-stretch lg:pr-3 w-full"
            >
                <BaseInputNumber
                    :model-value="getModelValue.energie.value"
                    input-id="building-info-general-dpe-energy"
                    class="w-full! min-w-0 lg:flex-1"
                    :min="0"
                    :max-fraction-digits="0"
                    :readonly="readonly && !disabled"
                    :disabled="disabled"
                    :placeholder="t('energySuffix')"
                    @update:model-value="onUpdateEnergyValue"
                />
                <div
                    class="relative flex h-10 min-h-10 w-full shrink-0 items-center justify-between rounded-sm px-2 sm:h-auto sm:min-h-0 lg:w-auto lg:min-w-28"
                    :class="getEnergyScaleTextClass"
                    :style="{ backgroundColor: getEnergyScale.color }"
                >
                    <span class="truncate text-xs">{{
                        getEnergyScale.range
                    }}</span>
                    <span class="shrink-0 pl-1 text-sm font-semibold">{{
                        getEnergyScale.letter
                    }}</span>
                    <span
                        class="pointer-events-none absolute -right-3 top-1/2 z-0 hidden h-0 w-0 -translate-y-1/2 border-y-[20px] border-l-[12px] border-y-transparent lg:block"
                        :style="{ borderLeftColor: getEnergyScale.color }"
                        aria-hidden="true"
                    >
                    </span>
                </div>
            </div>
        </BaseInputWrapper>

        <!-- ================================ -->
        <!-- DPE GES -->
        <!-- ================================ -->
        <BaseInputWrapper
            input-id="building-info-general-dpe-ges"
            :label="t('gesLabel')"
            :hint="displayHint ? t('gesHint') : ''"
            unfloat
        >
            <div
                class="flex min-w-0 flex-col gap-2 lg:flex-row lg:items-stretch w-full"
            >
                <BaseInputNumber
                    :model-value="getModelValue.ges.value"
                    input-id="building-info-general-dpe-ges"
                    class="w-full! min-w-0 lg:flex-1"
                    :min="0"
                    :max-fraction-digits="0"
                    :readonly="readonly && !disabled"
                    :disabled="disabled"
                    :placeholder="t('gesSuffix')"
                    @update:model-value="onUpdateGesValue"
                />
                <div
                    class="flex h-10 min-h-10 w-full shrink-0 items-center justify-between rounded-sm px-2 sm:h-auto sm:min-h-0 lg:w-auto lg:min-w-28"
                    :class="getGesScaleTextClass"
                    :style="{ backgroundColor: getGesScale.color }"
                >
                    <span class="truncate text-xs">{{
                        getGesScale.range
                    }}</span>
                    <span class="shrink-0 pl-1 text-sm font-semibold">{{
                        getGesScale.letter
                    }}</span>
                </div>
            </div>
        </BaseInputWrapper>

        <!-- ================================ -->
        <!-- DATE DPE -->
        <!-- ================================ -->
        <BaseInputWrapper
            input-id="building-info-general-dpe-date"
            :label="t('dateLabel')"
            :hint="displayHint ? t('dateHint') : ''"
            unfloat
        >
            <BaseDatePicker
                input-id="building-info-general-dpe-date"
                :model-value="getDateValue"
                class="w-full"
                date-format="dd/mm/yy"
                :disabled="readonly || disabled"
                @update:model-value="onUpdateDate"
            />
        </BaseInputWrapper>
    </div>
</template>
